import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import { useTranslation } from 'react-i18next';
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { fetchFiles } from "../redux/actions/fileActions";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { enGB } from 'date-fns/locale';

import { actionButtonStyles } from "../styles/actionButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { textFieldStyles } from "../styles/textFieldStyles";
import {
  Container,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import { CustomPaper } from "../styles/customPaper";
import { mediumTitle, smallTitle } from "../styles/typography";
const ReportGenerator = () => {
  const { t } = useTranslation(['pages', 'common', 'buttons']);

  const typeOptions = [
    t('pages:lawTypes.liabilityLaw'),
    t('pages:lawTypes.civilLaw'),
    t('pages:lawTypes.laborLaw'),
    t('pages:lawTypes.administrativeLaw'),
    t('pages:lawTypes.taxLaw'),
    t('pages:lawTypes.constructionLaw'),
    t('pages:lawTypes.rentalResidentialLaw'),
    t('pages:lawTypes.environmentalLaw'),
    t('pages:lawTypes.corporateLaw'),
    t('pages:lawTypes.militaryLaw'),
    t('pages:lawTypes.educationLaw'),
    t('pages:lawTypes.other'),
    t('pages:lawTypes.personalFamilyLaw'),
    t('pages:lawTypes.legalEntityLaw'),
    t('pages:lawTypes.socialSecurityLaw'),
    t('pages:lawTypes.criminalLaw'),
    t('pages:lawTypes.disciplinaryLaw'),
    t('pages:lawTypes.lawOfObligations'),
    t('pages:lawTypes.insuranceLaw'),
    t('pages:lawTypes.immigrationLaw'),
    t('pages:lawTypes.commercialLaw'),
  ];
  const dispatch = useDispatch();

  // Initialize reportType from sessionStorage or default to "caseFiles"
  const [reportType, setReportType] = useState(() => {
    const savedReportType = sessionStorage.getItem('reportGenerator_reportType');
    console.log("Initializing reportType from sessionStorage:", savedReportType);
    return savedReportType || "caseFiles";
  });

  const [exportFormat, setExportFormat] = useState(() => {
    const savedExportFormat = sessionStorage.getItem('reportGenerator_exportFormat');
    return savedExportFormat || "pdf";
  });
  const { relationships } = useSelector((state) => state.relationshipList);
  const { files } = useSelector((state) => state.fileList);
  const [filters, setFilters] = useState({
    status: "Active",
    startDate: "",
    endDate: "",
    lawyerId: "",
    jurisdiction: "",
    caseId: "",
  });

  useEffect(() => {
    dispatch(fetchRelationships());
    dispatch(fetchFiles());
  }, [dispatch]);

  // Persist reportType to sessionStorage when it changes
  useEffect(() => {
    console.log("ReportType changed to:", reportType);
    sessionStorage.setItem('reportGenerator_reportType', reportType);
  }, [reportType]);

  // Reset status filter when report type changes to ensure appropriate default values
  useEffect(() => {
    console.log("Resetting status filter for reportType:", reportType);
    let defaultStatus = "";

    switch (reportType) {
      case "relationships":
      case "caseFiles":
      case "caseFilesWithTimesheets":
        defaultStatus = "Active";
        break;
      case "timesheets":
        defaultStatus = ""; // Let user choose between billed/not billed
        break;
      case "invoices":
        defaultStatus = ""; // Let user choose between paid/unpaid/partially paid
        break;
      default:
        defaultStatus = "";
    }

    console.log("Setting status filter to:", defaultStatus);
    setFilters(prevFilters => {
      console.log("Previous filters:", prevFilters);
      const newFilters = {
        ...prevFilters,
        status: defaultStatus
      };
      console.log("New filters:", newFilters);
      return newFilters;
    });
  }, [reportType]);

  // Persist exportFormat to sessionStorage when it changes
  useEffect(() => {
    console.log("ExportFormat changed to:", exportFormat);
    sessionStorage.setItem('reportGenerator_exportFormat', exportFormat);
  }, [exportFormat]);





  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Custom setter for reportType that ensures persistence
  const updateReportType = (newReportType) => {
    console.log("Updating reportType from", reportType, "to", newReportType);
    setReportType(newReportType);
    sessionStorage.setItem('reportGenerator_reportType', newReportType);
  };

  // Custom setter for exportFormat that ensures persistence
  const updateExportFormat = (newExportFormat) => {
    console.log("Updating exportFormat from", exportFormat, "to", newExportFormat);
    setExportFormat(newExportFormat);
    sessionStorage.setItem('reportGenerator_exportFormat', newExportFormat);
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log("Report generation started with reportType:", reportType);
    console.log("Current filters being sent:", filters);

    // Verify reportType from sessionStorage matches current state
    const savedReportType = sessionStorage.getItem('reportGenerator_reportType');
    if (savedReportType && savedReportType !== reportType) {
      console.warn("ReportType mismatch detected! Restoring from sessionStorage:", savedReportType);
      setReportType(savedReportType);
      return; // Exit and let user try again with correct state
    }

    try {
      console.log("Sending request with:", { reportType, filters, exportFormat });
      const response = await axios.post(
        "/api/generate-report",
        { reportType, filters, exportFormat },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Set appropriate file extension based on export format
      const fileExtension = exportFormat === 'word' ? '.docx' : '.pdf';
      const fileName = `${reportType}_report_${new Date().toISOString().split('T')[0]}${fileExtension}`;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("Report generation completed successfully with reportType:", reportType);

      // Verify state persistence after successful generation
      const finalReportType = sessionStorage.getItem('reportGenerator_reportType');
      if (finalReportType !== reportType) {
        console.warn("State mismatch after generation! Restoring reportType:", finalReportType);
        setReportType(finalReportType);
      }
    } catch (error) {
      console.error("Error generating report:", error);
      console.log("Report generation failed with reportType:", reportType);

      // Verify state persistence after error
      const finalReportType = sessionStorage.getItem('reportGenerator_reportType');
      if (finalReportType !== reportType) {
        console.warn("State mismatch after error! Restoring reportType:", finalReportType);
        setReportType(finalReportType);
      }
      // You could add a notification here if you have a notification system
    }
  };

  const handleGenerateReport = async () => {
    console.log("Generate report button clicked with reportType:", reportType);
    await handleSubmit();
  };

  const handleAutocompleteChange = (name) => (event, value) => {
    // Extract the ID from the selected object for backend compatibility
    let filterValue = value;
    if (name === 'lawyerId' && value) {
      filterValue = value._id || value.id || value;
    } else if (name === 'caseId' && value) {
      filterValue = value._id || value.id || value;
    }

    console.log(`Filter ${name} changed:`, { original: value, processed: filterValue });
    setFilters({ ...filters, [name]: filterValue });
  };

  const statusOptions = [
    { label: t('common:active'), value: "Active" },
    { label: t('common:inactive'), value: "Inactive" },
  ];

  const statusOptions2 = [
    { label: t('pages:reports.select_status'), value: "" },
    { label: t('pages:reports.billed'), value: "true" },
    { label: t('pages:reports.not_billed'), value: "false" },
  ];


  const statusOptions3 = [
    { label: t('pages:reports.paid'), value: "paid" },
    { label: t('pages:reports.partially_paid'), value: "partially paid" },
    { label: t('pages:reports.unpaid'), value: "unpaid" },
  ];

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Parse date string to Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  // Handle date change from date picker
  const handleDateChange = (name) => (date) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      setFilters({ ...filters, [name]: formattedDate });
    } else {
      setFilters({ ...filters, [name]: '' });
    }
  };

  const renderDatePicker = (name, label) => (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <DatePicker
        label={label}
        value={parseDate(filters[name])}
        onChange={handleDateChange(name)}
        slotProps={{
          textField: {
            fullWidth: true,
            margin: "normal",
            size: "small",
            sx: {
              ...textFieldStyles({ marginRight: "0" }),
              marginTop: "-16px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "0px",
                height: "34px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
                transform: "translate(14px, 9px) scale(1)",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -6px) scale(0.75)",
                }
              },
              "& .MuiInputBase-input": {
                padding: "8px 14px",
                fontSize: "0.75rem",
                fontFamily: "'Fira Sans', 'Helvetica Neue', Helvetica, Roboto, Arial, sans-serif",
              },
              "& .MuiIconButton-root": {
                padding: "4px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d1d1d1",
              }
            }
          }
        }}
        format="dd/MM/yyyy"
      />
    </LocalizationProvider>
  );

  const renderFilters = () => {
    switch (reportType) {
      case "relationships":
        return (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  options={statusOptions}
                  getOptionLabel={(option) => option.label}
                  value={(() => {
                    const foundOption = statusOptions.find(
                      (option) => option.value === filters.status
                    );
                    const selectedValue = foundOption || statusOptions[0];
                    console.log("Relationships status filter - filters.status:", filters.status, "selectedValue:", selectedValue);
                    return selectedValue;
                  })()}
                  onChange={(event, newValue) => {
                    const statusValue = newValue ? newValue.value : "Active"; // Default to Active for relationships
                    handleInputChange({
                      target: {
                        name: "status",
                        value: statusValue,
                      },
                    });
                  }}
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common:status')}
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                        marginTop: "-16px",
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("startDate", t('pages:reports.start_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("endDate", t('pages:reports.end_date'))}
              </FormControl>
            </Grid>
          </>
        );
      case "caseFiles":
        return (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  options={statusOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    statusOptions.find(
                      (option) => option.value === filters.status
                    ) || statusOptions[0]
                  }
                  onChange={(event, newValue) => {
                    const statusValue = newValue ? newValue.value : "Active"; // Default to Active for case files
                    handleInputChange({
                      target: {
                        name: "status",
                        value: statusValue,
                      },
                    });
                  }}
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common:status')}
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                        marginTop: "-16px",
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("startDate", t('pages:reports.start_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("endDate", t('pages:reports.end_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <Autocomplete
                options={relationships?.filter(
                  (relationship) => relationship.type === "Lawyer"
                )}
                getOptionLabel={(option) =>
                  `${option.uniqueNumber} - ${option.firstName} ${option.lastName}`
                }
                value={relationships?.find(rel => rel._id === filters.lawyerId) || null}
                onChange={handleAutocompleteChange("lawyerId")}
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label={t('pages:reports.lawyer')}
                    name="lawyerId"
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <Autocomplete
                options={typeOptions}
                value={filters.jurisdiction}
                onChange={handleAutocompleteChange("jurisdiction")}
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label={t('pages:reports.jurisdiction')}
                    name="jurisdiction"
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                  />
                )}
              />
            </Grid>
          </>
        );
      case "timesheets":
        return (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  options={statusOptions2}
                  getOptionLabel={(option) => option.label}
                  value={
                    statusOptions2.find(
                      (option) => option.value === filters.status
                    ) || statusOptions2[0]
                  }
                  onChange={(event, newValue) =>
                    handleInputChange({
                      target: {
                        name: "status",
                        value: newValue ? newValue.value : "",
                      },
                    })
                  }
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                        marginTop: "-16px",
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("startDate", t('pages:reports.start_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("endDate", t('pages:reports.end_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <Autocomplete
                options={relationships?.filter(
                  (relationship) => relationship.type === "Lawyer"
                )}
                getOptionLabel={(option) =>
                  `${option.uniqueNumber} - ${option.firstName} ${option.lastName}`
                }
                value={relationships?.find(rel => rel._id === filters.lawyerId) || null}
                onChange={handleAutocompleteChange("lawyerId")}
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label="Lawyer"
                    name="lawyerId"
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                  />
                )}
              />
            </Grid>
            {(reportType === "timesheets" || reportType === "invoices") && (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Autocomplete
                  options={files}
                  getOptionLabel={(option) =>
                    `${option?.fileNo} - ${option?.name}`
                  }
                  value={files?.find(file => file._id === filters.caseId) || null}
                  onChange={handleAutocompleteChange("caseId")}
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="normal"
                      label={t('pages:reports.case')}
                      name="caseId"
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </>
        );
      case "invoices":
        return (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <FormControl fullWidth margin="normal">
                <Autocomplete
                  options={statusOptions3}
                  getOptionLabel={(option) => option.label}
                  value={
                    statusOptions3.find(
                      (option) => option.value === filters.status
                    ) || statusOptions3[0]
                  }
                  onChange={(event, newValue) =>
                    handleInputChange({
                      target: {
                        name: "status",
                        value: newValue ? newValue.value : "",
                      },
                    })
                  }
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                        marginTop: "-16px",
                      }}
                    />
                  )}
                />
              </FormControl>
              
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("startDate", t('pages:reports.start_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("endDate", t('pages:reports.end_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <Autocomplete
                options={relationships?.filter(
                  (relationship) => relationship.type === "Lawyer"
                )}
                getOptionLabel={(option) =>
                  `${option.uniqueNumber} - ${option.firstName} ${option.lastName}`
                }
                value={relationships?.find(rel => rel._id === filters.lawyerId) || null}
                onChange={handleAutocompleteChange("lawyerId")}
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label="Lawyer"
                    name="lawyerId"
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                  />
                )}
              />
            </Grid>
            {(reportType === "timesheets" || reportType === "invoices") && (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                <Autocomplete
                  options={files}
                  getOptionLabel={(option) =>
                    `${option.fileNo} - ${option.name}`
                  }
                  value={files?.find(file => file._id === filters.caseId) || null}
                  onChange={handleAutocompleteChange("caseId")}
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="normal"
                      label={t('pages:reports.case')}
                      name="caseId"
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </>
        );
      case "caseFilesWithTimesheets":
        return (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  options={statusOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    statusOptions.find(
                      (option) => option.value === filters.status
                    ) || statusOptions[0]
                  }
                  onChange={(event, newValue) => {
                    const statusValue = newValue ? newValue.value : "Active"; // Default to Active for case files with timesheets
                    handleInputChange({
                      target: {
                        name: "status",
                        value: statusValue,
                      },
                    });
                  }}
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common:status')}
                      size="small"
                      sx={{
                        ...textFieldStyles({ marginRight: "0" }),
                        marginTop: "-16px",
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("startDate", t('pages:reports.start_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <FormControl fullWidth margin="normal">
                {renderDatePicker("endDate", t('pages:reports.end_date'))}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <Autocomplete
                options={relationships?.filter(
                  (relationship) => relationship.type === "Lawyer"
                )}
                getOptionLabel={(option) =>
                  `${option.uniqueNumber} - ${option.firstName} ${option.lastName}`
                }
                value={relationships?.find(rel => rel._id === filters.lawyerId) || null}
                onChange={handleAutocompleteChange("lawyerId")}
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label={t('pages:reports.lawyer')}
                    name="lawyerId"
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
              <Autocomplete
                options={typeOptions}
                value={filters.jurisdiction}
                onChange={handleAutocompleteChange("jurisdiction")}
                PaperComponent={CustomPaper}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label={t('pages:reports.jurisdiction')}
                    name="jurisdiction"
                    size="small"
                    sx={{
                      ...textFieldStyles({ marginRight: "0" }),
                    }}
                  />
                )}
              />
            </Grid>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", // Reduced shadow spread
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:reports.generate_report')} />

        {/* 
        <Button
          startIcon={<AddIcon />}
          onClick={() => navigate("/timesheets/create")}
          size="small"
          sx={actionButtonStyles()}
        >
          Create Timesheet
        </Button> */}
      </Box>

      <div>
        <Box elevation={3} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{...mediumTitle}}>
            {t('pages:reports.report_type')}
          </Typography>
          <FormControl fullWidth margin="normal" size="small" sx={{marginTop:"0"}}>
            {/* <InputLabel>Report Type</InputLabel> */}
            <Select
              value={reportType}
              onChange={(e) => {
                console.log("User selected reportType:", e.target.value);
                updateReportType(e.target.value);
              }}
              size="small"
              sx={{ ...textFieldStyles({ width: "25%", marginRight: "0" }) }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: "0px 3px 12px rgba(0, 0, 0, 0.1)",
                    borderRadius: "0",
                  },
                },
              }}
            >
              <MenuItem value="relationships">
                {t('pages:reports.overview_relationships')}
              </MenuItem>
              <MenuItem value="caseFiles">{t('pages:reports.overview_case_files')}</MenuItem>
              <MenuItem value="timesheets">{t('pages:reports.overview_timesheets')}</MenuItem>
              <MenuItem value="invoices">{t('pages:reports.overview_invoices')}</MenuItem>
              <MenuItem value="caseFilesWithTimesheets">{t('pages:reports.case_files_with_timesheets')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box elevation={3} sx={{ p: 2, mb: 3, mt: "-20px" }}>
          <Typography variant="h6" gutterBottom sx={{...mediumTitle}}>
            Export Format
          </Typography>
          <FormControl fullWidth margin="normal" size="small" sx={{marginTop:"0"}}>
            <Select
              value={exportFormat}
              onChange={(e) => updateExportFormat(e.target.value)}
              size="small"
              sx={{ ...textFieldStyles({ width: "25%", marginRight: "0" }) }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    boxShadow: "0px 3px 12px rgba(0, 0, 0, 0.1)",
                    borderRadius: "0",
                  },
                },
              }}
            >
              <MenuItem value="pdf">
                PDF Document
              </MenuItem>
              <MenuItem value="word">
                Word Document (.docx)
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box elevation={3} sx={{ p: 2, mb: 3, mt: "-20px" }}>
          <Typography variant="h6" gutterBottom sx={{...mediumTitle, marginBottom: 1}}>
            {t('pages:reports.filters')}
          </Typography>
          <Grid container spacing={2}>
            {renderFilters()}
          </Grid>
        </Box>
        <Box sx={{padding: "0 20px 40px 20px"}}>
          <Button
            type="button"
            onClick={handleGenerateReport}
            sx={actionButtonStyles}
            fullWidth
          >
            {t('pages:reports.generate_report')}
          </Button>
        </Box>
      </div>
    </Container>
  );
};

export default ReportGenerator;
